/** AddProductModal — fast entry point (Dashboard Home / Quotation / anywhere) into the ONE authoritative
 *  Product Master → Add product experience. No separate form: it renders the shared ProductFormModal. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ProductFormModal } from "@/components/products/ProductFormModal";
import { CheckCircle2, Plus, ExternalLink, FileText, LayoutDashboard } from "lucide-react";

/* props:
 *  onClose            — dismiss
 *  onCreated(product) — called with the saved Product Master record (quotation: line is added immediately)
 *  prefill            — optional partial product (e.g. { oem_part_number } typed in a quote search) → Product Intelligence auto-enriches
 *  context            — label shown under the header ("Dashboard quick action", "Quotation")
 *  returnChoices      — true (Dashboard): after save show ADD ANOTHER / OPEN PRODUCT / CREATE QUOTE / RETURN TO DASHBOARD instead of auto-closing */
export function AddProductModal({ onClose, onCreated, prefill = null, context = "Quick action", returnChoices = false }) {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const pickExisting = async (id) => {
    try {
      const { data } = await api.get(`/product-master/products/${id}`);
      onCreated?.(data);
    } catch { navigate(`/technology/product-master?product=${id}`); onClose?.(); }
  };

  if (saved) {
    return (
      <div className="fixed inset-0 z-[70] bg-slate-900/40 grid place-items-center p-4" data-testid="add-product-done">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5">
          <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="text-sm font-heading font-semibold">Product saved to Product Master</span></div>
          <div className="mt-1 text-[12px] text-slate-600" data-testid="add-product-done-name"><b>{saved.product_name}</b>{saved.product_code ? <> · <span className="font-mono">{saved.product_code}</span></> : null}{saved.oem_part_number ? <> · {saved.oem_part_number}</> : null}</div>
          <div className="mt-1 text-[11px] text-slate-500">Available now in Quotation, Catalogue, Product Intelligence and Global Search.</div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => { setSaved(null); setFormKey((k) => k + 1); }} data-testid="add-product-another"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add another product</Button>
            <Button variant="outline" onClick={() => { onClose?.(); navigate(`/technology/product-master?product=${saved.id}`); }} data-testid="add-product-open"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open product</Button>
            <Button variant="outline" onClick={() => { onClose?.(); sessionStorage.setItem("st:quote-prefill", JSON.stringify({ items: [{ line_type: "PRODUCT", product_id: saved.id, product_code: saved.product_code, part_number: saved.oem_part_number, product_name: saved.product_name, description: saved.description, sku: saved.sku, quantity: 1, unit_price_inr: saved.standard_selling_price ?? 0, tax_pct: saved.gst_percent ?? 18 }] })); navigate("/commercial/crm/quotations?new=1&prefill=1"); }} data-testid="add-product-quote"><FileText className="h-3.5 w-3.5 mr-1.5" /> Create quote</Button>
            <Button onClick={() => onClose?.()} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-product-return"><LayoutDashboard className="h-3.5 w-3.5 mr-1.5" /> Return to dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProductFormModal
      key={formKey}
      mode="create"
      context={context}
      initial={prefill ? { ...prefill } : null}
      onClose={onClose}
      onSaved={(p) => {
        if (returnChoices) { setSaved(p); onCreated?.(p); return; }
        onCreated?.(p);
      }}
      onViewExisting={(id) => { onClose?.(); navigate(`/technology/product-master?product=${id}`); }}
      onUseExisting={returnChoices ? undefined : (id) => { toast.info("Using the existing Product Master record"); pickExisting(id); }}
    />
  );
}

export default AddProductModal;
